import _ from 'lodash';
import MoviesService from './movies.service';
import ImdbMovie from './imdb-movie.schema';


/**
 * Parses IMDB GraphQL API response into an ImdbMovie object.
 */
export default class ImdbGraphQLParser {

  static get ValidContentRatings() {
    return MoviesService.MPAARatingOptions;
  }

  imdbMovieId = null;
  data = null;

  constructor(graphqlData, imdbMovieId = null) {
    this.imdbMovieId = imdbMovieId;
    // graphqlData is the response from the GraphQL API: { data: { title: { ... } } }
    this.data = _.get(graphqlData, 'data.title') || _.get(graphqlData, 'title') || null;
  }

  parseAll() {
    let movie = null;
    if (this.data) {
      movie = new ImdbMovie();
      movie.imdb_id = this.imdbMovieId;

      movie.title = this.getTitle();
      movie.release_date = this.getReleaseDate();
      movie.release_year = this.getYear();

      let posterUrls = this.getPosterUrls();
      movie.imdb_img = _.first(posterUrls) || null;
      movie.imdb_imgs = posterUrls;

      movie.rating = this.getContentRating();
      movie.duration_mins = this.getRuntimeMinutes();
      movie.synopsis = this.getSynopsis();
      movie.star_rating = this.getStarRating();
      movie.metascore = this.getMetaScore();

      movie.all_genres = this.getGenres();
      movie.all_writers = this.getWriters();
      movie.all_directors = this.getDirectors();
      movie.all_cast = this.getCast();

      movie.genre = _.join(movie.all_genres, ', ');
      movie.cast = _.join(movie.all_cast, ', ');
    }
    return movie;
  }

  getTitle() {
    return _.get(this.data, 'titleText.text') || null;
  }

  getSynopsis() {
    return _.get(this.data, 'plot.plotText.plainText') || null;
  }

  getYear() {
    return _.get(this.data, 'releaseYear.year') || null;
  }

  getReleaseDate() {
    let rd = _.get(this.data, 'releaseDate');
    if (rd && rd.year) {
      let month = _.padStart(String(rd.month || 1), 2, '0');
      let day = _.padStart(String(rd.day || 1), 2, '0');
      return `${rd.year}-${month}-${day}`;
    }
    return null;
  }

  getPosterUrls() {
    let url = _.get(this.data, 'primaryImage.url');
    if (!url) { return []; }
    // remove IMDB's resize suffix to get full-size image
    let fullUrl = _.replace(url, /\._[^.]+_(\.\w+)$/, '$1');
    return _.chain([fullUrl, url])
      .map(_.trim)
      .uniq()
      .compact()
      .value();
  }

  getRuntimeMinutes() {
    let seconds = _.get(this.data, 'runtime.seconds');
    if (_.isFinite(seconds) && seconds > 0) {
      return Math.round(seconds / 60);
    }
    return null;
  }

  getStarRating() {
    let val = _.get(this.data, 'ratingsSummary.aggregateRating');
    return _.isFinite(val) ? val : null;
  }

  getMetaScore() {
    let val = _.get(this.data, 'metacritic.metascore.score');
    return _.isFinite(val) ? val : null;
  }

  getContentRating() {
    let str = _.get(this.data, 'certificate.rating');
    let rating = !_.isEmpty(str) ? _.trim(str) : null;

    let validRatings = this.constructor.ValidContentRatings;
    if (rating && !_.isEmpty(validRatings)) {
      let validRating = _.find(validRatings, v => {
        return (_.toLower(v.label) === _.toLower(rating));
      });
      rating = _.get(validRating, 'value') || null;
    }
    return rating;
  }

  getGenres() {
    let genres = _.get(this.data, 'genres.genres');
    let items = _.chain(genres)
      .map(g => _.get(g, 'text'))
      .compact()
      .uniq()
      .slice(0, 3)
      .value();
    return !_.isEmpty(items) ? items : null;
  }

  _extractCreditNames(creditEdges) {
    let items = _.chain(creditEdges)
      .map(edge => _.get(edge, 'node.name.nameText.text'))
      .map(_.trim)
      .compact()
      .uniq()
      .slice(0, 3)
      .value();
    return !_.isEmpty(items) ? items : null;
  }

  getDirectors() {
    return this._extractCreditNames(_.get(this.data, 'directors.edges'));
  }

  getWriters() {
    return this._extractCreditNames(_.get(this.data, 'writers.edges'));
  }

  getCast() {
    return this._extractCreditNames(_.get(this.data, 'cast.edges'));
  }
}
